import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  useTheme,
} from "@mui/material";
import { useAuth } from "../../../context";
import useFetchObjects from "../../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReportPdfLayout from "../../../Components/ReportPdfLayout";

const formatCurrency = (val) => {
  if (val == null || val === undefined) return "—";
  const n = Number(val);
  if (isNaN(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const LabOverallReport = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const auth = useAuth();
  const token = auth?.user?.token;

  const today = new Date();
  const defaultEnd = new Date(today);
  const defaultStart = new Date(today);
  defaultStart.setDate(defaultStart.getDate() - 30);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.append("start_date", startDate.toISOString().slice(0, 10));
    p.append("end_date", endDate.toISOString().slice(0, 10));
    return p.toString();
  }, [startDate, endDate]);

  const endpoint = `reports/lab_overall/?${params}`;
  const { data, isLoading, isError, error, refetch } = useFetchObjects(
    ["reports-lab-overall", params],
    endpoint,
    token
  );

  const handleApply = () => refetch();

  const summary = data?.summary ?? {};
  const byDoctor = data?.by_doctor ?? [];
  const orders = data?.orders ?? [];
  const dateRange = data?.date_range ?? {};

  const cards = [
    { label: t("reports.totalOrders"), value: summary.total_orders },
    { label: t("reports.totalTests"), value: summary.total_tests },
    { label: t("reports.pendingOrders"), value: summary.pending_orders },
    { label: t("reports.completedOrders"), value: summary.completed_orders },
    { label: t("reports.totalAmountPaid"), value: summary.total_amount_paid, isCurrency: true },
    { label: t("reports.totalAmountUnpaid"), value: summary.total_amount_unpaid, isCurrency: true },
  ];

  return (
    <Grid container maxWidth="xl" sx={{ p: 3, width: "100%" }}>
      <Grid item xs={12}>
        <ReportPdfLayout
          reportTitle={t("reports.labOverallReport")}
          fileName="lab-overall-report.pdf"
          disabled={isLoading || isError || !data}
        >
          <Grid container>
            <Grid item xs={12} className="no-print">
              <Typography variant="h5" fontWeight={600} gutterBottom>
                {t("reports.labOverallReport")}
              </Typography>
            </Grid>
            <Grid item xs={12} mb={2} className="no-print">
              <Paper sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
          <DatePicker
            selected={startDate}
            onChange={(d) => setStartDate(d)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            customInput={
              <TextField size="small" label={t("reports.startDate")} sx={{ minWidth: 160 }} />
            }
          />
          <DatePicker
            selected={endDate}
            onChange={(d) => setEndDate(d)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            customInput={
              <TextField size="small" label={t("reports.endDate")} sx={{ minWidth: 160 }} />
            }
          />
          <Button variant="contained" onClick={handleApply}>
            {t("reports.apply")}
          </Button>
              </Paper>
            </Grid>
                  {isError && (
              <Grid item xs={12}>
                <Alert severity="error">{error?.message || t("reports.errorLoading")}</Alert>
              </Grid>
            )}
            {isLoading && (
              <Grid item xs={12} display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Grid>
            )}
            {!isLoading && !isError && data && (
        <>
          <Grid item xs={12} mb={2}>
            <Typography variant="body1">
              {t("reports.dateRange")}: {dateRange.start} — {dateRange.end}
            </Typography>
          </Grid>

          <Grid item xs={12} container spacing={2} mb={3}>
            {cards.map((c) => (
              <Grid item xs={12} sm={6} md={4} key={c.label}>
                <Paper
                  sx={{
                    p: 2,
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {c.label}
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {c.isCurrency ? formatCurrency(c.value) : (c.value ?? "—")}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {byDoctor.length > 0 && (
            <Grid item xs={12} mb={3}>
              <Typography variant="h6" gutterBottom>
                {t("reports.byDoctor")}
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: "divider" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: theme.palette.primary.main }}>
                      <TableCell sx={{ color: "white", fontWeight: 600 }}>
                        {t("reports.doctorName")}
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: 600 }} align="right">
                        {t("reports.orderCount")}
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: 600 }} align="right">
                        {t("reports.testCount")}
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: 600 }} align="right">
                        {t("reports.totalAmount")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byDoctor.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row.doctor_name || "—"}</TableCell>
                        <TableCell align="right">{row.order_count ?? 0}</TableCell>
                        <TableCell align="right">{row.test_count ?? 0}</TableCell>
                        <TableCell align="right">{formatCurrency(row.total_amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}

          {orders.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("reports.ordersList")}
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: "divider" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: theme.palette.primary.main }}>
                      <TableCell sx={{ color: "white", fontWeight: 600 }}>
                        {t("reports.patientName")}
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: 600 }}>
                        {t("reports.ticketNumber")}
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: 600 }}>
                        {t("reports.orderDate")}
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: 600 }} align="right">
                        {t("reports.testCount")}
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: 600 }} align="right">
                        {t("reports.totalAmount")}
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: 600 }}>
                        {t("reports.status")}
                      </TableCell>
                      <TableCell sx={{ color: "white", fontWeight: 600 }}>
                        {t("reports.isPaid")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.patient_name ?? "—"}</TableCell>
                        <TableCell>{row.ticket_number ?? "—"}</TableCell>
                        <TableCell>{row.order_date ?? "—"}</TableCell>
                        <TableCell align="right">{row.test_count ?? 0}</TableCell>
                        <TableCell align="right">{formatCurrency(row.total_amount)}</TableCell>
                        <TableCell>{row.status ?? "—"}</TableCell>
                        <TableCell>{row.is_paid ? t("reports.yes") : t("reports.no")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
        </>
            )}
          </Grid>
        </ReportPdfLayout>
      </Grid>
    </Grid>
  );
};

export default LabOverallReport;
